import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import QRCodeModal from '../../components/ui/QRCodeModal';

interface PropertyMedia {
  id: number;
  file_url: string;
  data_url: string;
  mime_type: string;
  sort_order: number;
  is_cover: boolean;
}

interface PropertyCompany {
  name: string;
  slug: string;
  logo_url: string | null;
  banner_url: string | null;
  description: string | null;
  phone: string | null;
  email: string | null;
  social_links: Record<string, string> | null;
}

interface PropertyDetail {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  property_type: string;
  operation_type: string;
  price: string;
  currency: string;
  expenses: string | null;
  address: string | null;
  city: string;
  province: string | null;
  country: string | null;
  surface_total: number | null;
  surface_covered: number | null;
  rooms: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  garage: number | null;
  floor: string | null;
  amenities: string[];
  views_count: number | null;
  published_at: string | null;
  media: PropertyMedia[];
  company: PropertyCompany;
}

interface PropertyDetailResponse {
  data: PropertyDetail;
}

function getMediaSrc(media: PropertyMedia): string {
  if (media.data_url) return media.data_url;
  const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');
  if (media.file_url.startsWith('http') || media.file_url.startsWith('data:')) return media.file_url;
  return `${API_BASE}${media.file_url}`;
}

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api').replace(/\/api\/?$/, '');

function mediaUrl(fileUrl: string): string {
  if (fileUrl.startsWith('http') || fileUrl.startsWith('data:')) return fileUrl;
  return `${API_BASE}${fileUrl}`;
}

function formatPrice(price: string | number, currency: string): string {
  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericPrice);
}

// --- Lightbox Component ---

function ImageLightbox({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}): React.ReactElement {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={onClose} />

      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Counter */}
      <div className="absolute top-4 left-4 z-10 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-white text-sm">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Previous button */}
      {images.length > 1 && (
        <button
          onClick={onPrev}
          className="absolute left-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Image */}
      <img
        src={images[currentIndex]}
        alt=""
        className="relative z-10 max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
      />

      {/* Next button */}
      {images.length > 1 && (
        <button
          onClick={onNext}
          className="absolute right-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-md flex items-center justify-center text-white transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
}

// --- Main Component ---

export default function PropertyDetailPage(): React.ReactElement {
  const { t } = useTranslation('catalog');
  const { companySlug, propertySlug } = useParams<{ companySlug: string; propertySlug: string }>();
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [inquiryBody, setInquiryBody] = useState('');
  const [qrOpen, setQrOpen] = useState(false);
  const [visitDate, setVisitDate] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    type: 'success',
  });

  // Auto-hide toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast((s) => ({ ...s, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // Fetch property
  const { data: propertyData, isLoading, error } = useQuery({
    queryKey: ['property-detail', companySlug, propertySlug],
    queryFn: () =>
      api.get<PropertyDetailResponse>(
        `/v1/public/catalog/${companySlug}/properties/${propertySlug}`
      ),
    enabled: !!companySlug && !!propertySlug,
  });

  const property = propertyData?.data;

  // Fetch favorites (if authenticated)
  const { data: favoritesData } = useQuery({
    queryKey: ['user-favorites'],
    queryFn: () => api.get<{ data: Array<{ property_id: number }> }>('/v1/favorites'),
    enabled: isAuthenticated,
  });

  const favoriteIds = new Set(favoritesData?.data?.map((f) => f.property_id) ?? []);
  const isFavorite = property ? favoriteIds.has(property.id) : false;

  // Toggle favorite
  const favoriteMutation = useMutation({
    mutationFn: async () => {
      if (!property) return;
      if (isFavorite) {
        return api.delete(`/v1/favorites/${property.id}`);
      }
      return api.post('/v1/favorites', { property_id: property.id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-favorites'] });
      setToast({
        show: true,
        message: isFavorite ? t('favorites.removed') : t('favorites.added'),
        type: 'success',
      });
    },
    onError: () => {
      setToast({ show: true, message: t('favorites.error'), type: 'error' });
    },
  });

  // Send inquiry
  const inquiryMutation = useMutation({
    mutationFn: (body: string) =>
      api.post('/v1/messages', {
        property_id: property?.id,
        body,
      }),
    onSuccess: () => {
      setInquiryBody('');
      setToast({ show: true, message: t('inquiry.success'), type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: t('inquiry.error'), type: 'error' });
    },
  });

  // Request visit
  const visitMutation = useMutation({
    mutationFn: (data: { property_id: number; scheduled_at: string; notes_client?: string }) =>
      api.post('/v1/visits', data),
    onSuccess: () => {
      setVisitDate('');
      setVisitNotes('');
      setToast({ show: true, message: t('visit.success'), type: 'success' });
    },
    onError: () => {
      setToast({ show: true, message: t('visit.error'), type: 'error' });
    },
  });

  const handleRequestVisit = (): void => {
    if (!property || !visitDate) return;
    visitMutation.mutate({
      property_id: property.id,
      scheduled_at: visitDate,
      notes_client: visitNotes.trim() || undefined,
    });
  };

  const handleCopyLink = (): void => {
    navigator.clipboard.writeText(window.location.href);
    setToast({ show: true, message: t('share.copied'), type: 'info' });
  };

  const handleSendInquiry = (): void => {
    if (inquiryBody.trim()) {
      inquiryMutation.mutate(inquiryBody.trim());
    }
  };

  const propertyUrl = `${window.location.origin}/c/${companySlug}/${propertySlug}`;

  const handleLightboxNext = useCallback((): void => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      const total = property?.media?.length || 0;
      return (prev + 1) % total;
    });
  }, [lightboxIndex, property?.media?.length]);

  const handleLightboxPrev = useCallback((): void => {
    if (lightboxIndex === null) return;
    setLightboxIndex((prev) => {
      if (prev === null) return null;
      const total = property?.media?.length || 0;
      return (prev - 1 + total) % total;
    });
  }, [lightboxIndex, property?.media?.length]);

  const handleLightboxClose = useCallback((): void => {
    setLightboxIndex(null);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-100">
        {/* Skeleton navbar */}
        <div className="bg-base-100 border-b border-base-content/5">
          <div className="container mx-auto px-6 py-4 flex items-center gap-3">
            <div className="skeleton w-10 h-10 rounded-xl" />
            <div className="skeleton w-40 h-5 rounded-lg" />
          </div>
        </div>
        <div className="container mx-auto px-6 py-8 max-w-7xl">
          {/* Skeleton gallery */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            <div className="md:col-span-2 skeleton h-[400px] rounded-2xl" />
            <div className="hidden md:flex flex-col gap-3">
              <div className="skeleton h-[195px] rounded-2xl" />
              <div className="skeleton h-[195px] rounded-2xl" />
            </div>
          </div>
          {/* Skeleton content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-4">
              <div className="skeleton h-10 w-3/4 rounded-lg" />
              <div className="skeleton h-6 w-1/2 rounded-lg" />
              <div className="skeleton h-8 w-1/3 rounded-lg" />
              <div className="skeleton h-40 w-full rounded-lg" />
            </div>
            <div className="space-y-4">
              <div className="skeleton h-48 rounded-2xl" />
              <div className="skeleton h-60 rounded-2xl" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error / not found
  if (error || !property) {
    return (
      <div className="min-h-screen bg-base-100">
        <div className="bg-base-100 border-b border-base-content/5">
          <div className="container mx-auto px-6 py-4">
            <span className="text-lg font-semibold">{t('title')}</span>
          </div>
        </div>
        <div className="container mx-auto px-6 max-w-2xl mt-24 text-center">
          <div className="w-20 h-20 rounded-2xl bg-error/10 flex items-center justify-center mx-auto mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold mb-3">{t('property.notFound')}</h2>
          <p className="text-base-content/60 mb-8">{t('property.notFoundDescription')}</p>
          <RouterLink
            to={`/c/${companySlug}`}
            className="btn btn-primary rounded-xl shadow-lg shadow-primary/20"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t('company.properties')}
          </RouterLink>
        </div>
      </div>
    );
  }

  // Sort media by sort_order, find cover image
  const sortedMedia = [...property.media].sort((a, b) => a.sort_order - b.sort_order);
  const allImageUrls = sortedMedia.map((m) => getMediaSrc(m));
  const coverMedia = sortedMedia.find((m) => m.is_cover) || sortedMedia[0] || null;
  const mainImage = coverMedia ? getMediaSrc(coverMedia) : null;
  const sideImages = allImageUrls.slice(1, 3);

  const propertyTypeLabel = t(`propertyTypes.${property.property_type}`, { defaultValue: property.property_type });
  const operationTypeLabel = t(`operationTypes.${property.operation_type}`, { defaultValue: property.operation_type });

  // Details rows for the table
  const detailRows: Array<{ icon: React.ReactNode; label: string; value: string }> = [];
  if (property.surface_total != null) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      ),
      label: t('property.surfaceTotal'),
      value: `${property.surface_total} ${t('property.sqm')}`,
    });
  }
  if (property.surface_covered != null) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
        </svg>
      ),
      label: t('property.surfaceCovered'),
      value: `${property.surface_covered} ${t('property.sqm')}`,
    });
  }
  if (property.rooms != null) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        </svg>
      ),
      label: t('property.rooms'),
      value: `${property.rooms}`,
    });
  }
  if (property.bedrooms != null) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      label: t('property.bedrooms'),
      value: `${property.bedrooms}`,
    });
  }
  if (property.bathrooms != null) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 10v8a2 2 0 002 2h14a2 2 0 002-2v-8M3 10V8a4 4 0 014-4h1" />
        </svg>
      ),
      label: t('property.bathrooms'),
      value: `${property.bathrooms}`,
    });
  }
  if (property.garage != null && property.garage > 0) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
      label: t('property.garage'),
      value: `${property.garage}`,
    });
  }
  if (property.floor) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h18M3 8h18M3 12h18M3 16h18M3 20h18" />
        </svg>
      ),
      label: t('property.floor'),
      value: property.floor,
    });
  }
  if (property.expenses) {
    detailRows.push({
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      label: t('property.expenses'),
      value: formatPrice(property.expenses, property.currency),
    });
  }

  // Quick stats for pill badges
  const quickStats: Array<{ label: string; value: string; icon: React.ReactNode }> = [];
  if (property.bedrooms != null) {
    quickStats.push({
      label: 'Dorm.',
      value: `${property.bedrooms}`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
    });
  }
  if (property.bathrooms != null) {
    quickStats.push({
      label: 'Ban.',
      value: `${property.bathrooms}`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M3 10v8a2 2 0 002 2h14a2 2 0 002-2v-8M3 10V8a4 4 0 014-4h1" />
        </svg>
      ),
    });
  }
  if (property.surface_total != null) {
    quickStats.push({
      label: t('property.sqm'),
      value: `${property.surface_total}`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
        </svg>
      ),
    });
  }
  if (property.garage != null && property.garage > 0) {
    quickStats.push({
      label: 'Coch.',
      value: `${property.garage}`,
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
    });
  }

  return (
    <div className="min-h-screen bg-base-100">
      {/* Top bar */}
      <div className="bg-base-100 border-b border-base-content/5 sticky top-0 z-40 backdrop-blur-xl bg-base-100/80">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              {property.company.logo_url ? (
                <div className="w-9 h-9 rounded-xl overflow-hidden shadow-sm border border-base-content/5">
                  <img
                    src={mediaUrl(property.company.logo_url)}
                    alt={property.company.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
                  <span className="text-primary-content font-bold text-xs">
                    {property.company.name.substring(0, 2).toUpperCase()}
                  </span>
                </div>
              )}
              <span className="font-semibold text-base-content/80">{property.company.name}</span>
            </div>
            <RouterLink
              to={`/c/${companySlug}`}
              className="btn btn-ghost btn-sm text-base-content/60 hover:text-primary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t('company.properties')}
            </RouterLink>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 max-w-7xl py-6">
        {/* Breadcrumbs */}
        <nav className="mb-6">
          <ol className="flex items-center gap-2 text-sm text-base-content/50">
            <li>
              <RouterLink to="/" className="hover:text-primary transition-colors">
                Home
              </RouterLink>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li>
              <RouterLink to={`/c/${companySlug}`} className="hover:text-primary transition-colors">
                {property.company.name}
              </RouterLink>
            </li>
            <li>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </li>
            <li className="text-base-content/80 font-medium truncate max-w-xs">
              {property.title}
            </li>
          </ol>
        </nav>

        {/* Image Gallery Hero */}
        {mainImage ? (
          <div className="relative mb-8">
            <div className="grid grid-cols-1 md:grid-cols-10 gap-3 rounded-2xl overflow-hidden">
              {/* Main image - ~70% */}
              <div
                className="md:col-span-7 relative cursor-pointer group overflow-hidden"
                onClick={() => setLightboxIndex(0)}
              >
                <img
                  src={mainImage}
                  alt={property.title}
                  className="w-full h-[300px] md:h-[480px] object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* Overlaid badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="px-3 py-1.5 rounded-lg bg-white/80 backdrop-blur-md text-sm font-semibold text-base-content shadow-sm">
                    {propertyTypeLabel}
                  </span>
                  <span className="px-3 py-1.5 rounded-lg bg-primary/90 backdrop-blur-md text-sm font-semibold text-primary-content shadow-sm">
                    {operationTypeLabel}
                  </span>
                </div>

                {/* Photo count badge */}
                {sortedMedia.length > 1 && (
                  <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-md text-white text-sm flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {sortedMedia.length} fotos
                  </div>
                )}
              </div>

              {/* Side thumbnails - ~30% */}
              {sideImages.length > 0 && (
                <div className="md:col-span-3 hidden md:flex flex-col gap-3">
                  {sideImages.map((url, idx) => (
                    <div
                      key={idx}
                      className="flex-1 cursor-pointer group overflow-hidden relative"
                      onClick={() => setLightboxIndex(idx + 1)}
                    >
                      <img
                        src={url}
                        alt={`${property.title} - ${idx + 2}`}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                      {/* Show "more photos" overlay on last thumb */}
                      {idx === sideImages.length - 1 && sortedMedia.length > 3 && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            +{sortedMedia.length - 3}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* If only 1 side image, add a "view all" card */}
                  {sideImages.length === 1 && sortedMedia.length > 2 && (
                    <div
                      className="flex-1 cursor-pointer bg-base-200 flex items-center justify-center rounded-none hover:bg-base-300 transition-colors"
                      onClick={() => setLightboxIndex(2)}
                    >
                      <div className="text-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mx-auto text-base-content/30 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-base-content/50">Ver todas</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile thumbnails strip */}
            {sortedMedia.length > 1 && (
              <div className="flex md:hidden gap-2 mt-3 overflow-x-auto pb-2 snap-x snap-mandatory">
                {sortedMedia.map((media, idx) => {
                  const url = getMediaSrc(media);
                  return (
                    <img
                      key={media.id}
                      src={url}
                      alt={`${property.title} - ${idx + 1}`}
                      onClick={() => setLightboxIndex(idx)}
                      className="snap-center shrink-0 w-20 h-16 object-cover rounded-lg cursor-pointer border-2 border-transparent hover:border-primary transition-colors"
                    />
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          /* No images placeholder */
          <div className="mb-8 rounded-2xl overflow-hidden">
            <div className="h-[300px] md:h-[400px] bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center relative">
              <div className="text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 mx-auto text-base-content/10 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                </svg>
                <p className="text-base-content/40 font-medium">{t('property.noImages')}</p>
              </div>
              {/* Overlaid badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="px-3 py-1.5 rounded-lg bg-white/80 backdrop-blur-md text-sm font-semibold text-base-content shadow-sm">
                  {propertyTypeLabel}
                </span>
                <span className="px-3 py-1.5 rounded-lg bg-primary/90 backdrop-blur-md text-sm font-semibold text-primary-content shadow-sm">
                  {operationTypeLabel}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Property Header */}
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              {/* Price */}
              <p className="text-3xl md:text-4xl font-extrabold text-primary mb-3">
                {formatPrice(property.price, property.currency)}
                {property.expenses && (
                  <span className="text-base font-normal text-base-content/40 ml-3">
                    + {formatPrice(property.expenses, property.currency)} exp.
                  </span>
                )}
              </p>

              {/* Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-base-content mb-3">
                {property.title}
              </h1>

              {/* Address */}
              {(property.address || property.city) && (
                <div className="flex items-center gap-2 text-base-content/60 mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>
                    {property.address && `${property.address}, `}
                    {property.city}
                    {property.province && `, ${property.province}`}
                    {property.country && ` (${property.country})`}
                  </span>
                </div>
              )}

              {/* Quick stat pills */}
              {quickStats.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {quickStats.map((stat, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-base-200 text-sm font-medium text-base-content/70"
                    >
                      <span className="text-primary">{stat.icon}</span>
                      <span className="font-semibold">{stat.value}</span>
                      <span className="text-base-content/40">{stat.label}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Views */}
              {property.views_count != null && (
                <p className="text-sm text-base-content/40 flex items-center gap-1.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {property.views_count} visitas
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={handleCopyLink}
                className="btn btn-ghost btn-sm rounded-xl border border-base-content/10 hover:border-primary hover:text-primary transition-all"
                title={t('share.copyLink')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                Compartir
              </button>
              <button
                onClick={() => setQrOpen(true)}
                className="btn btn-ghost btn-sm rounded-xl border border-base-content/10 hover:border-primary hover:text-primary transition-all"
                title="QR Code"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                </svg>
                QR
              </button>
              {isAuthenticated && (
                <button
                  onClick={() => favoriteMutation.mutate()}
                  className={`btn btn-sm rounded-xl border transition-all ${
                    isFavorite
                      ? 'bg-red-50 border-red-200 text-red-500 hover:bg-red-100'
                      : 'btn-ghost border-base-content/10 hover:border-red-300 hover:text-red-500'
                  }`}
                  aria-label={isFavorite ? t('favorites.remove') : t('favorites.add')}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill={isFavorite ? 'currentColor' : 'none'}
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  {isFavorite ? 'Guardado' : 'Guardar'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left column - 2/3 */}
          <div className="lg:col-span-2 space-y-8">
            {/* Description */}
            {property.description && (
              <div className="bg-base-100 rounded-2xl border border-base-content/5 p-8 shadow-sm">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h7" />
                  </svg>
                  {t('property.description')}
                </h2>
                <div
                  className="prose prose-sm max-w-none prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 text-base-content/80 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: property.description }}
                />
              </div>
            )}

            {/* Details table */}
            {detailRows.length > 0 && (
              <div className="bg-base-100 rounded-2xl border border-base-content/5 p-8 shadow-sm">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  {t('property.details')}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                  {detailRows.map((row, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-4 px-4 py-4 ${
                        idx % 2 === 0 ? 'bg-base-200/30' : ''
                      } ${idx < 2 ? 'rounded-t-xl' : ''} ${
                        idx >= detailRows.length - 2 ? 'rounded-b-xl' : ''
                      }`}
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary shrink-0">
                        {row.icon}
                      </div>
                      <div>
                        <p className="text-xs text-base-content/40 uppercase tracking-wide">{row.label}</p>
                        <p className="font-semibold">{row.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amenities */}
            {property.amenities && property.amenities.length > 0 && (
              <div className="bg-base-100 rounded-2xl border border-base-content/5 p-8 shadow-sm">
                <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  {t('property.amenities')}
                </h2>
                <div className="flex flex-wrap gap-2">
                  {property.amenities.map((amenity) => (
                    <span
                      key={amenity}
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-base-200/60 text-sm font-medium text-base-content/70 hover:bg-primary/10 hover:text-primary transition-colors cursor-default"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-success" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      {amenity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* More from this company */}
            <div className="bg-base-100 rounded-2xl border border-base-content/5 p-8 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  Mas propiedades de {property.company.name}
                </h2>
                <RouterLink
                  to={`/c/${companySlug}`}
                  className="btn btn-ghost btn-sm text-primary hover:bg-primary/10"
                >
                  Ver todas
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </RouterLink>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[1, 2].map((i) => (
                  <div key={i} className="rounded-xl bg-base-200/40 p-6 text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-base-content/10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4" />
                    </svg>
                    <p className="text-sm text-base-content/30">Explora mas propiedades</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column - 1/3 sticky sidebar */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 space-y-6">
              {/* Company card */}
              <div className="bg-base-100 rounded-2xl border border-base-content/5 shadow-lg overflow-hidden">
                {/* Header gradient */}
                <div className="h-16 bg-gradient-to-r from-primary/10 to-secondary/10" />
                <div className="px-6 pb-6 -mt-8">
                  <div className="flex items-end gap-4 mb-4">
                    {property.company.logo_url ? (
                      <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg border-4 border-base-100 bg-base-100">
                        <img
                          src={mediaUrl(property.company.logo_url)}
                          alt={property.company.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shadow-lg border-4 border-base-100">
                        <span className="text-primary-content font-bold text-lg">
                          {property.company.name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg truncate">{property.company.name}</h3>
                    </div>
                  </div>

                  {property.company.description && (
                    <p className="text-sm text-base-content/60 mb-4 line-clamp-2">{property.company.description}</p>
                  )}

                  <div className="space-y-3 mb-4">
                    {property.company.phone && (
                      <a
                        href={`tel:${property.company.phone}`}
                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        <span>{property.company.phone}</span>
                      </a>
                    )}
                    {property.company.email && (
                      <a
                        href={`mailto:${property.company.email}`}
                        className="flex items-center gap-3 text-sm hover:text-primary transition-colors group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <span className="truncate">{property.company.email}</span>
                      </a>
                    )}
                  </div>

                  {property.company.social_links && Object.keys(property.company.social_links).length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {Object.entries(property.company.social_links).map(([platform, url]) => (
                        <a
                          key={platform}
                          href={url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1 rounded-lg bg-base-200/60 text-xs font-medium capitalize hover:bg-primary/10 hover:text-primary transition-colors"
                        >
                          {platform}
                        </a>
                      ))}
                    </div>
                  )}

                  <RouterLink
                    to={`/c/${companySlug}`}
                    className="btn btn-primary btn-sm w-full rounded-xl shadow-md shadow-primary/20"
                  >
                    Ver catalogo completo
                  </RouterLink>
                </div>
              </div>

              {/* Inquiry form */}
              <div className="bg-base-100 rounded-2xl border border-base-content/5 shadow-lg p-6">
                <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  {t('inquiry.title')}
                </h3>
                {isAuthenticated ? (
                  <div>
                    <textarea
                      className="textarea textarea-bordered w-full rounded-xl mb-3 focus:textarea-primary transition-colors"
                      rows={4}
                      placeholder={t('inquiry.bodyPlaceholder')}
                      value={inquiryBody}
                      onChange={(e) => setInquiryBody(e.target.value)}
                    />
                    <button
                      className="btn btn-primary w-full rounded-xl shadow-md shadow-primary/20 hover:shadow-primary/40 transition-all"
                      onClick={handleSendInquiry}
                      disabled={!inquiryBody.trim() || inquiryMutation.isPending}
                    >
                      {inquiryMutation.isPending ? (
                        <span className="loading loading-spinner loading-sm" />
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                          </svg>
                          {t('inquiry.submit')}
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-base-200/30 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-base-content/15 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    <p className="text-sm text-base-content/50 mb-3">
                      {t('inquiry.loginRequired')}
                    </p>
                    <RouterLink to="/login" className="btn btn-outline btn-sm rounded-xl btn-primary">
                      {t('inquiry.loginLink')}
                    </RouterLink>
                  </div>
                )}
              </div>

              {/* Schedule Visit */}
              {isAuthenticated && (
                <div className="bg-base-100 rounded-2xl border border-base-content/5 shadow-lg p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {t('visit.title')}
                  </h3>
                  <div className="space-y-4">
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text text-sm font-medium">
                          {t('visit.dateLabel')} *
                        </span>
                      </label>
                      <input
                        type="datetime-local"
                        className="input input-bordered w-full rounded-xl focus:input-secondary transition-colors"
                        value={visitDate}
                        onChange={(e) => setVisitDate(e.target.value)}
                      />
                    </div>
                    <div className="form-control w-full">
                      <label className="label">
                        <span className="label-text text-sm font-medium">
                          {t('visit.notesLabel')}
                        </span>
                      </label>
                      <textarea
                        className="textarea textarea-bordered w-full rounded-xl focus:textarea-secondary transition-colors"
                        rows={3}
                        placeholder={t('visit.notesPlaceholder')}
                        value={visitNotes}
                        onChange={(e) => setVisitNotes(e.target.value)}
                      />
                    </div>
                    <button
                      className="btn btn-secondary w-full rounded-xl shadow-md shadow-secondary/20 hover:shadow-secondary/40 transition-all"
                      onClick={handleRequestVisit}
                      disabled={!visitDate || visitMutation.isPending}
                    >
                      {visitMutation.isPending ? (
                        <span className="loading loading-spinner loading-sm" />
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {t('visit.submit')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && allImageUrls.length > 0 && (
        <ImageLightbox
          images={allImageUrls}
          currentIndex={lightboxIndex}
          onClose={handleLightboxClose}
          onNext={handleLightboxNext}
          onPrev={handleLightboxPrev}
        />
      )}

      {/* QR Code modal */}
      <QRCodeModal
        url={propertyUrl}
        title={property.title}
        isOpen={qrOpen}
        onClose={() => setQrOpen(false)}
      />

      {/* Toast */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-50 animate-[slideUp_0.3s_ease-out]">
          <div className={`flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl backdrop-blur-md border ${
            toast.type === 'success'
              ? 'bg-success/90 text-success-content border-success/20'
              : toast.type === 'error'
                ? 'bg-error/90 text-error-content border-error/20'
                : 'bg-info/90 text-info-content border-info/20'
          }`}>
            {toast.type === 'success' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
            {toast.type === 'error' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            )}
            {toast.type === 'info' && (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              className="ml-2 hover:opacity-70 transition-opacity"
              onClick={() => setToast((s) => ({ ...s, show: false }))}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
